





















































































































import { Component, Prop, Vue } from "vue-property-decorator";
import axios from "axios";
import { EventBus } from "../event-bus";
import { Account } from "@/models/Account";
import { DeletedProfile } from "@/models/DeletedProfile";
import { Token } from "../utils/Token";
import { Error } from "../utils/Error";

@Component({
  components: {},
  filters: {
    transformDeviceType(value: number) {
      return value ? `SCT-${value}` : "";
    }
  }
})
export default class DeletedProfilesModal extends Vue {
  @Prop() readonly value!: boolean;
  // Disables "Recreate" while a USB device is being created.
  @Prop() readonly recreateDisabled!: boolean;

  private profiles: Array<DeletedProfile> = [];
  private activeUsers: Array<Account> = [];
  private search = "";
  private loading = false;

  private duplicateModalFlag = false;
  private duplicateMatches: Array<Account> = [];
  private pendingProfile: DeletedProfile | null = null;

  private errorModalFlag = false;
  private errorText = "";
  private errorTitle = "Error";

  private headers = [
    {
      text: "Name",
      align: "start",
      value: "fullName",
      sortable: true
    },
    {
      text: "Location",
      value: "location",
      sortable: true,
      align: "start"
    },
    {
      text: "Email",
      value: "email",
      sortable: true,
      align: "start"
    },
    {
      text: "IPv4 Address",
      value: "ipv4Address",
      sortable: true,
      align: "start"
    },
    {
      text: "Device",
      value: "serialNumber",
      sortable: true,
      align: "start"
    },
    {
      text: "Deleted On",
      value: "dateDeleted",
      sortable: true,
      align: "start"
    },
    {
      text: "Actions",
      value: "actions",
      sortable: false,
      align: "center"
    }
  ];

  get show() {
    return this.value;
  }
  set show(value: boolean) {
    EventBus.$emit("closeDeletedProfilesModal", value);
  }

  created() {
    EventBus.$on("openDeletedProfilesModal", this.onOpen);
  }

  beforeDestroy() {
    EventBus.$off("openDeletedProfilesModal", this.onOpen);
    EventBus.$off("closeErrorModal", this.onCloseErrorModal);
  }

  // activeUsers are the non-deleted profiles currently listed in DeviceListing.
  private onOpen(activeUsers: Array<Account>) {
    this.activeUsers = activeUsers || [];
    this.search = "";
    this.fetchProfiles();
  }

  private onCloseErrorModal(value: boolean) {
    this.errorModalFlag = value;
  }

  private showError(text: string) {
    this.errorText = text;
    // Other modals clear every closeErrorModal listener when they are created,
    // so (re)register ours right before the error modal is shown.
    EventBus.$off("closeErrorModal", this.onCloseErrorModal);
    EventBus.$on("closeErrorModal", this.onCloseErrorModal);
    this.errorModalFlag = true;
  }

  private async fetchProfiles() {
    this.loading = true;
    this.profiles = [];
    const token = await Token.getTokenWithExpiry("token");

    axios({
      method: "get",
      withCredentials: true,
      url: `${process.env.VUE_APP_BASE_URL}/devices/deleted`,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-type": "application/json"
      }
    })
      .then(response => {
        this.loading = false;
        if (response.data.code == 401) {
          EventBus.$emit("LogOutEventBus", false);
        } else if (response.data.code == 200) {
          this.profiles = (response.data.results || []).map(
            (p: DeletedProfile) =>
              new DeletedProfile(
                p.accountId,
                p.firstName,
                p.lastName,
                p.email,
                p.location,
                p.description,
                p.deviceType,
                p.serialNumber,
                p.ipv4Address,
                p.netMask,
                p.gateway,
                p.altIpv4Address,
                p.altNetMask,
                p.altGateway,
                p.dateDeleted
              )
          );
        } else {
          this.showError(
            "There was an error in fetching the deleted profiles."
          );
        }
      })
      .catch(err => {
        this.loading = false;
        console.log("error:", err);
        if (err.response && err.response.status == 401) {
          Error.handleError(err);
          return;
        }
        this.showError("There was an error in fetching the deleted profiles.");
      });
  }

  // Active (non-deleted) profiles that share the location or email of the
  // given deleted profile.
  private activeMatches(profile: DeletedProfile): Array<Account> {
    const normalize = (v: string) => (v || "").trim().toLowerCase();
    const email = normalize(profile.email);
    const location = normalize(profile.location);

    return this.activeUsers.filter(
      (user: Account) =>
        (email !== "" && normalize(user.email) === email) ||
        (location !== "" && normalize(user.location) === location)
    );
  }

  private recreate(profile: DeletedProfile) {
    const matches = this.activeMatches(profile);
    if (matches.length > 0) {
      // Non-blocking: ask the operator to confirm before recreating.
      this.pendingProfile = profile;
      this.duplicateMatches = matches;
      this.duplicateModalFlag = true;
      return;
    }

    this.openRecreate(profile);
  }

  private confirmDuplicate() {
    const profile = this.pendingProfile;
    this.cancelDuplicate();
    if (profile) {
      this.openRecreate(profile);
    }
  }

  private cancelDuplicate() {
    this.duplicateModalFlag = false;
    this.duplicateMatches = [];
    this.pendingProfile = null;
  }

  // DeviceListing closes this modal and opens CreateDevice prefilled.
  private openRecreate(profile: DeletedProfile) {
    EventBus.$emit("recreateDevice", profile);
  }

  // close modal and clear the list
  private cancel(): void {
    this.show = false;
    this.profiles = [];
    this.search = "";
  }
}
